import React, { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  /**
   * When true (default) the next page is fetched automatically as soon as the
   * sentinel scrolls into view (infinite scroll). The button remains as a
   * manual fallback.
   */
  auto?: boolean;
  /**
   * The scrollable ancestor to observe intersections against. Pass the ref of
   * the fixed-height scroll container so pages load only when the user scrolls
   * to the bottom. Omit to observe against the viewport.
   */
  scrollRootRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Infinite-scroll trigger for the keyset/cursor-paginated SEPA lists. An
 * IntersectionObserver auto-loads the next page when the sentinel becomes
 * visible; the "Mehr laden" button is kept as a manual fallback. Renders nothing
 * when there is no further page.
 */
const CursorLoadMore: React.FC<Props> = ({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  auto = true,
  scrollRootRef,
}) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!auto || !hasNextPage) return;
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          onLoadMore();
        }
      },
      { root: scrollRootRef?.current ?? null, rootMargin: "120px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [auto, hasNextPage, isFetchingNextPage, onLoadMore, scrollRootRef]);

  if (!hasNextPage) return null;

  return (
    <div ref={sentinelRef} className="flex justify-center py-3">
      <Button
        variant="outline"
        className="text-sm"
        onClick={onLoadMore}
        disabled={isFetchingNextPage}
      >
        {isFetchingNextPage ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Wird geladen…
          </>
        ) : (
          "Mehr laden"
        )}
      </Button>
    </div>
  );
};

export default CursorLoadMore;
